import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { fetchJson } from '../../../helpers';
import {cloneDeep, merge} from 'lodash';
import deepEqual from 'deep-equal';
import { widgetWithDatasource } from '../WidgetLibraryGen2';

import {
  dashboard_get_widgets_path,
  dashboard_clone_widget_path,
  dashboard_create_widget_path,
  dashboard_update_settings_path,
  dashboard_update_layout_path,
  dashboard_update_global_theme_path
} from '~/routes';

const COLS = 24;
const ROW_HEIGHT = 25;

const initialState = {
  dashboardId: null,
  projectId: null,
  loading: false,
  error: null,
  originalState: null,
  editingId: null,
  sidebarOpen: false,
  savedLayout: {},
  dashboardType: 'internal',
  hideRefresh: false,
  theme: {},
  widgets: {},
  layout:{},
  available_widgets: [],
  colorScheme: [],
  asPdf: false,
  showProfilePics: false,
  cols: COLS,
  rowHeight: ROW_HEIGHT,
  chartOptions: {
    chartTypeId: 'bar'
  },
  modalOpen: false,
  disableRefresh: false,
  hidden_items: [],
  detailOpenId: false,
  configErrors: {},
  contentBlockData: {},
  // Datasource v2: widgets may run on it here / the user may configure it (see DashboardController)
  datasourceV2Enabled: false,
  datasourceV2Editable: false
};

export const INITIAL_WIDGET_DIMENSION = {
    w: 8,
    h: 3
}

const createValidatedConfigErrorThunk = (typePrefix, payloadCreator) => {
  return createAsyncThunk(
    typePrefix,
    async (arg, thunkAPI) => {
      const configErrors = thunkAPI.getState().dashboard.configErrors;
      if (Object.keys(configErrors).length > 0) {
        window.ToastNotification({message: I18n.t('error_setting_sidebar'), horizontal: 'center', vertical: 'top', autoHideDuration: 5});
        return;
      }
      return payloadCreator(arg, thunkAPI);
    }
  );
};
export const fetchDashboardInitialState = createAsyncThunk(
    'dashboard/fetchDashboard',
    async ({ projectId, dashboardId, widgetSettings, ...rest }, { rejectWithValue}) => {
        try {
            // Public dashboard has settings provided
            const settings = widgetSettings || await fetchJson(dashboard_get_widgets_path({project_id: projectId, id: dashboardId, _options: true}));
            return {
                ...{projectId, dashboardId, ...rest},
                fetchData: settings
            }
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
);

export const saveTheme = createValidatedConfigErrorThunk(
    'dashboard/saveTheme',
    async (_, { getState, rejectWithValue}) => {
        try {
            const { theme, dashboardId } = getState().dashboard;
            await fetchJson(dashboard_update_global_theme_path({id: dashboardId}), {body: JSON.stringify({theme: theme}), method: 'POST'})
            return;
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
)

export const saveWidgetSettings = createValidatedConfigErrorThunk(
    'dashboard/saveWidgetSettings',
    async (_, { getState, rejectWithValue}) => {
        try {
            const {editingId, widgets, dashboardId} = getState().dashboard;
            const {id, ...rest} = widgets[editingId];
            let body = {...rest}
            if (!widgetWithDatasource.includes(rest.widget_type)){
                delete body.data_datasource
            }
            return await fetchJson(dashboard_update_settings_path({dashboard_id: dashboardId, widget_id: editingId}), {body: JSON.stringify(body), method: 'POST'});
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
)

// Tab container widget saves widget settings outside of side panel in order to update widgetId in tab.
export const saveWidgetSettingsWithId = createValidatedConfigErrorThunk(
    'dashboard/saveWidgetSettingsWithId',
    async ( {widgetId, closeSidebar = true}, { getState, rejectWithValue, dispatch}) => {
        try {
            const {widgets, dashboardId} = getState().dashboard;
            const {id, ...rest} = widgets[widgetId];
            let body = {...rest}
            if (!widgetWithDatasource.includes(rest.widget_type)){
                delete body.data_datasource
            }
            await fetchJson(dashboard_update_settings_path({dashboard_id: dashboardId, widget_id: widgetId}), {body: JSON.stringify(body), method: 'POST'});
            return;
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
)

export const saveLayout = createAsyncThunk(
    'dashboard/saveCurrentLayout',
    async (_, { getState, rejectWithValue}) => {
        try {
            const {dashboardId, layout, hidden_items: sentHiddenItems} = getState().dashboard;
            const { deleted_items, hidden_items } = await fetchJson(dashboard_update_layout_path({id: dashboardId}), {
                body: JSON.stringify({layout: Object.values(layout), hidden_items: sentHiddenItems}), method: 'POST'
            })

            // Container widgets contain extra deleted items
            const deleted = new Set(deleted_items.map(Number));

            return {
                layout: Object.fromEntries(Object.entries(layout).filter(([id]) => !deleted.has(Number(id)))),
                hidden_items
            };
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
)

export const createWidget = createAsyncThunk(
    'dashboard/createWidget',
    async ({type, layoutItem}, { getState, rejectWithValue}) => {
        try {
            const {dashboardId} = getState().dashboard;
            const res = await fetchJson(dashboard_create_widget_path({dashboard_id: dashboardId, type: type}), {method: "POST"});
            return {
                widget: res,
                layout: {...layoutItem, ...INITIAL_WIDGET_DIMENSION, i: `widget-${res.id}`, isDraggable: false}
            }
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
)

export const cloneWidget = createAsyncThunk(
    'dashboard/cloneWidget',
    async ({widgetId, layoutItem, disconnectFromCloned = false}, { getState, rejectWithValue}) => {
        try {
            const {dashboardId} = getState().dashboard;
            const res = await fetchJson(dashboard_clone_widget_path({
                dashboard_id: dashboardId, widget_id: widgetId, disconnect_from_cloned: disconnectFromCloned
            }), {method: "POST"});
            if (!res) { 
                throw('Cloning widget failed.');
            }
            return {
                widget: res,
                layout: {...layoutItem, ...INITIAL_WIDGET_DIMENSION, i: `widget-${res.id}`, isDraggable: false},
                ...(disconnectFromCloned ? {deleteWidgetId: widgetId} : {})
            }
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
)

export const deleteWidget = createAsyncThunk(
    'dashboard/deleteWidget',
    async (widgetId, { getState, rejectWithValue }) => {
        try {
            const {dashboardId, layout} = getState().dashboard;
            const { [widgetId]: _, ...removedWidgetLayout } = layout;

            const { deleted_items, hidden_items } = await fetchJson(dashboard_update_layout_path(
                {id: dashboardId}), {method: 'POST', body: JSON.stringify({layout: Object.values(removedWidgetLayout)})}
            );

            // Container widgets contain extra deleted items
            const deleted = new Set(deleted_items.map(Number));

            return {
                layout: Object.fromEntries(Object.entries(layout).filter(([id]) => !deleted.has(Number(id)))),
                hidden_items
            };
        } catch(e) {
            return rejectWithValue(e.message)
        }
    }
)

const thunkMap = [
    {
        thunk: fetchDashboardInitialState,
        fulfilled: (state, action) => {
            state.loading = false;
            if (action.payload){
                const {fetchData, projectId, dashboardId, dashboardType, hideRefresh, colorScheme, asPdf, showProfilePics, disableRefresh, datasourceV2Enabled, datasourceV2Editable} = action.payload || {}
                const defaultTheme = {
                    params: { fontWeight: 'normal' },
                    overrides: { wrapping: 'never', fontStyle: 'normal', fontWeight: 'normal' }
                }
                state.theme = merge(defaultTheme, fetchData.theme);
                state.widgets = fetchData.widgets.reduce((acc,item) => {acc[item.id] = item; return acc;}, {});
                state.available_widgets = fetchData.available_widgets;
                state.layout = fetchData.layout.reduce((acc,item) => {  
                    const key = item.i.split('-')[1];
                    acc[key] = item;
                    return acc;}, {});
                state.projectId = projectId;
                state.dashboardId = dashboardId;
                state.dashboardType = dashboardType;
                state.hideRefresh = hideRefresh;
                state.colorScheme = colorScheme;
                state.asPdf = asPdf;
                state.showProfilePics = showProfilePics;
                state.disableRefresh = disableRefresh;
                state.datasourceV2Enabled = !!datasourceV2Enabled;
                state.datasourceV2Editable = !!datasourceV2Editable;
                state.hidden_items = fetchData.hidden_items;
            }
        }
    },
    {
        thunk: saveTheme,
        fulfilled: (state, action) => {
            state.loading = false;
            state.originalState = null;
        }
    },
    {
        thunk: saveWidgetSettings,
        fulfilled: (state, action) => {
            state.loading = false;
            // When creating new resources like datasource, the id is provided by backend so need to save back to
            // widget setting
            state.widgets[action.payload?.id] = action.payload
            state.originalState = null;
        }
    },
    {
        thunk: saveWidgetSettingsWithId,
        fulfilled: (state, action) => {
            state.loading = false;
            state.originalState = null;
        }
    },
    {
        thunk: createWidget,
        fulfilled: (state, action) => {
            state.loading = false;
            const {widget, layout} = action.payload
            state.widgets[widget.id] = widget;
            state.layout[widget.id] = layout
        }
    },
    {
        thunk: cloneWidget,
        fulfilled: (state, action) => {
            state.loading = false;
            const {widget, layout, deleteWidgetId} = action.payload

            // Removing widget it was cloned from
            if (deleteWidgetId) {
                delete state.widgets[deleteWidgetId];
                delete state.layout[deleteWidgetId];
            }

            state.widgets[widget.id] = widget;
            state.layout[widget.id] = layout;
        }
    },
    {
        thunk: saveLayout,
        fulfilled: (state, action) => {
            state.loading = false;
            state.layout = action.payload.layout;
            state.hidden_items = action.payload.hidden_items;
        }
    },
    {
        thunk: deleteWidget,
        fulfilled: (state, action) => {
            state.loading = false;
            state.layout = action.payload.layout;
            state.hidden_items = action.payload.hidden_items;
            if (state.sidebarOpen){
                state.sidebarOpen = false;
            }

            if (state.editingId){
                state.editingId = null;
            }
        }
    }
]

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Update deeply nested setting, example use: updateWidget({id: widgetId, path: 'theme.overrides.common.title.color', '#ffffff'})
    updateWidgetAttribute(state, action) {
        const { id, path, value } = action.payload;
        const pathParts = path.split('.');
        
        // Ensure widget exists
        if (!state.widgets[id]) {
            state.widgets[id] = {};
        }
        
        let node = state.widgets[id];
        
        // Navigate to the parent of the target property
        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            if (!node[part]) {
                node[part] = {};
            }
            node = node[part];
        }
        
        // Set the final value
        node[pathParts[pathParts.length - 1]] = value;
    },

    updateTheme(state, action) {
      state.theme = { ...state.theme, ...action.payload };
    },

    updateThemeAttribute(state, action) {
        const { path, value } = action.payload;
        const pathParts = path.split('.');

        let node = state.theme;

        // Navigate to the parent of the target property
        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            if (!node[part]) {
                node[part] = {};
            }
            node = node[part];
        }

        // Set the final value
        node[pathParts[pathParts.length - 1]] = value;
    },

    updateArrayEntityAttribute(state, action) {
        const { widgetId, entitiesSettingId, entityId, index = null, path, value } = action.payload;

        // Ensure widget exists
        if (!state.widgets[widgetId]) {
            state.widgets[widgetId] = {};
        }
        const entities = [...state.widgets[widgetId].settings[entitiesSettingId]];
        const pathParts = path.split('.');

        const idx = index || entities.findIndex(s => s.id === entityId);
        let node = entities[idx];

        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            if (!node[part]) {
                node[part] = {};
            }
            node = node[part];
        }
        node[pathParts[pathParts.length - 1]] = value;
    },

    updateSeriesAttribute(state, action) {
        const { widgetId, seriesId, path, value } = action.payload;

        // Ensure widget exists
        if (!state.widgets[widgetId]) {
            state.widgets[widgetId] = {};
        }
        const series = [...state.widgets[widgetId].settings.series];
        const pathParts = path.split('.');

        const index = series.findIndex(s => s.id === seriesId);
        let node = series[index];

        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            if (!node[part]) {
                node[part] = {};
            }
            node = node[part];
        }

        node[pathParts[pathParts.length - 1]] = value;
    },

    openSidebarWidget(state, action) {
        state.editingId = action.payload;
        state.originalState = cloneDeep(state.widgets[action.payload])
        state.sidebarOpen = true;
    },

    openSidebarTheme(state){
        state.editingId = null;
        state.sidebarOpen = true;
        state.originalState = cloneDeep(state.theme);
    },

    closeSidebar(state) {
        const { editingId, originalState } = state;
        if (originalState){
            const newState = editingId ? state.widgets[editingId] : state.theme;
            if (!deepEqual(newState, originalState) && !confirm(I18n.t('cancel_confirm'))) {
                return;
            }
            if (editingId) {
                state.widgets[editingId] = originalState
            } else {
                state.theme = originalState
            }
        }

        // Reset state
        state.editingId = null;
        state.originalState = null;
        state.sidebarOpen = false;
        state.detailOpenId = false;
        state.configErrors = {};
    },

    expandWidget(state, action) {
        if (state.savedLayout[action.payload]){
            state.layout[action.payload] = {
                ...state.layout[action.payload],
                ...state.savedLayout[action.payload]
            }
            state.savedLayout = {};
        } else {
            state.savedLayout[action.payload] = state.layout[action.payload];
            state.layout[action.payload] = {
                ...state.layout[action.payload],
                x: 0, w: 12, h: 12
            }
        }

    },

    applyContentBlockData(state, action) {
        const { targetWidgetId, data } = action.payload;
        state.contentBlockData[targetWidgetId] = data;
    },

    openModal(state, action) {
        state.modalOpen = true;
    },

    closeModal(state, action) {
        state.modalOpen = false;
    },

    openDetail(state, action){
        state.detailOpenId = action.payload;
    },

    closeDetail(state, action){
        state.detailOpenId = null;
    },

    setWidgetDraggable(state, action){
        state.layout[action.payload.widgetId] = {
            ...state.layout[action.payload.widgetId],
            isDraggable: action.payload.isDraggable
        }
    },

    updateLayout(state, action){
        const newLayout = action.payload.reduce((acc,item) => {  
                const key = item.i.split('-')[1];
                acc[key] = item;
                return acc;}, {});
        state.layout = {
            ...state.layout,
            ...newLayout
        }
    },

    addHiddenItem(state, action) {
        state.hidden_items = [
            ...state.hidden_items,
            action.payload
        ];
    },

    updateConfigError(state, action) {
        const { id, path, value } = action.payload;

        if (!state.configErrors[id]) {
            state.configErrors[id] = {};
        }

        if (path === '') {
            if (value === null) {
                delete state.configErrors[id];
            } else {
                state.configErrors[id] = value;
            }
        } else {
            const pathParts = path.split('.');
            let node = state.configErrors[id];
            const nodesStack = [node];

            for (let i = 0; i < pathParts.length - 1; i++) {
                const part = pathParts[i];
                if (!node[part]) node[part] = {};
                node = node[part];
                nodesStack.push(node);
            }

            const lastPart = pathParts[pathParts.length - 1];

            if (value === null) {
                delete node[lastPart];
            } else {
                node[lastPart] = value;
            }

            // Recursively delete error key if error is empty
            for (let i = nodesStack.length - 1; i >= 0; i--) {
                const currentNode = nodesStack[i];
                const keys = Object.keys(currentNode);
                if (keys.length === 0) {
                if (i === 0) {
                    delete state.configErrors[id];
                } else {
                    const parentNode = nodesStack[i - 1];
                    const keyToDelete = pathParts[i - 1];
                    delete parentNode[keyToDelete];
                }
                } else {
                break;
                }
            }
        }
    },

    // Base attribute are API call that can be shared across components
    updateBaseAttribute(state, action) {
        state[action.payload.key] = action.payload.value;
    }
  },
  extraReducers: (builders) => {
    thunkMap.map(({thunk, fulfilled}) => {
        builders
        .addCase(thunk.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(thunk.fulfilled, fulfilled)
        .addCase(thunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    })
  }
});

export const {
  updateWidgetAttribute,
  updateTheme,
  openSidebarWidget,
  closeSidebar,
  expandWidget,
  setWidgetDraggable,
  openSidebarTheme,
  updateLayout,
  updateBaseAttribute,
  updateThemeAttribute,
  updateSeriesAttribute,
  openModal,
  closeModal,
  openDetail,
  closeDetail,
  addHiddenItem,
  updateConfigError,
  updateArrayEntityAttribute,
  applyContentBlockData
} = dashboardSlice.actions;

export default dashboardSlice.reducer;